/**
 * Buy buttons — intercepts product form submit, POSTs /cart/add.js, then
 * dispatches `product_atc_via_form` so the quick cart can open without a
 * full-page cart navigation. Guards against overselling when inventory is
 * tracked and continue-selling is off.
 */
(() => {
  if (window.__themeBuyButtons) return;
  window.__themeBuyButtons = true;

  const ROOT = window.Shopify?.routes?.root || '/';
  const enqueueCartMutation = (() => {
    if (window.themeEnqueueCartMutation) return window.themeEnqueueCartMutation;

    let queue = Promise.resolve();
    window.themeEnqueueCartMutation = (mutation) => {
      const next = queue.then(mutation, mutation);
      queue = next.catch(() => {});
      return next;
    };

    return window.themeEnqueueCartMutation;
  })();

  const setBusy = (button, busy) => {
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.classList.toggle('buy-buttons__submit--loading', busy);
  };

  const flashError = (button) => {
    if (!(button instanceof HTMLElement)) return;

    button.classList.add('buy-buttons__submit--error');
    window.setTimeout(() => {
      button.classList.remove('buy-buttons__submit--error');
    }, 1600);
  };

  const quantitySelectorFor = (form) => form.querySelector('quantity-selector');

  const variantForForm = (form) => {
    const host =
      form.closest('product-form') || form.closest('[data-product-form]');

    return (
      host?.getCurrentVariant?.() ||
      host?.querySelector('product-variant-picker')?.getCurrentVariant?.() ||
      null
    );
  };

  const clampQuantityToLimits = (form) => {
    const selector = quantitySelectorFor(form);
    const limits = window.themeQuantityLimitsForVariant?.(variantForForm(form));

    if (selector && typeof selector.setLimits === 'function' && limits) {
      selector.setLimits(limits);
      return;
    }

    if (selector && typeof selector.setMax === 'function' && limits?.max != null) {
      selector.setMax(limits.max);
    }
  };

  const announceStatus = (buyButtons, message) => {
    if (!(buyButtons instanceof HTMLElement) || !message) return;

    const status = buyButtons.querySelector('[data-buy-buttons-status]');

    if (!(status instanceof HTMLElement)) return;

    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  };

  const announceInventoryError = (buyButtons) => {
    announceStatus(buyButtons, buyButtons?.dataset?.inventoryError);
  };

  const giftItemsFor = (paidVariantId, giftVariantId) => {
    const paid = Number(paidVariantId) || 0;
    const gift = Number(giftVariantId) || 0;

    if (gift <= 0 || gift === paid) return [];
    if (window.themeFreeGift?.isPromotionSuppressed?.(String(gift))) return [];

    return [
      {
        id: gift,
        quantity: 1,
        properties: {
          _gwp: 'yes',
          _gwp_promotion: String(gift),
        },
      },
    ];
  };

  const addToCart = async (form) => {
    const formData = new FormData(form);
    const id = formData.get('id');
    const quantity = Number(formData.get('quantity') || '1') || 1;
    const sellingPlan = Number(formData.get('selling_plan') || 0);

    if (!id) {
      throw new Error('Missing variant id');
    }

    const selector = quantitySelectorFor(form);
    const ruleError = selector?.getRuleError?.();

    if (ruleError) {
      const error = new Error(ruleError);

      error.code = 'quantity_rule';
      throw error;
    }

    const limits = window.themeQuantityLimitsForVariant?.(variantForForm(form));

    if (limits?.max != null && quantity > limits.max) {
      const error = new Error('Inventory exceeded');

      error.code = 'inventory';
      throw error;
    }

    const item =
      sellingPlan > 0
        ? { id: Number(id), quantity, selling_plan: sellingPlan }
        : { id: Number(id), quantity };

    const host =
      form.closest('product-form') || form.closest('[data-product-form]');
    const items = [
      item,
      ...giftItemsFor(id, host?.getAttribute?.('data-free-gift-variant-id')),
    ];

    const postItems = (payloadItems) =>
      enqueueCartMutation(() =>
        fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: payloadItems }),
        }),
      );

    let response = await postItems(items);
    let data = await response.json().catch(() => ({}));

    if (!response.ok && items.length > 1) {
      response = await postItems([item]);
      data = await response.json().catch(() => ({}));
    }

    if (!response.ok) {
      // A rejected add can still put part of the quantity in the cart (up to a
      // stock or basket limit), so ask quick cart and the header count to catch up.
      document.dispatchEvent(new CustomEvent('cart_refresh'));

      const message = data.description || data.message || 'Unable to add to cart';
      const error = new Error(message);

      // Match the stock wording specifically. A bare `available` also matches
      // rejections that have nothing to do with stock, such as a pre-order line
      // that needs its selling plan, and mislabels them as an inventory problem.
      if (/sold out|out of stock|not enough|in your cart|you can only add|inventor/i.test(message)) {
        error.code = 'inventory';
      } else if (/minimum|maximum|increment|quantity/i.test(message)) {
        error.code = 'quantity_rule';
      }

      throw error;
    }

    return data;
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      const buyButtons = form.querySelector('.buy-buttons');

      if (!buyButtons) return;

      if (buyButtons.classList.contains('buy-buttons--notify')) {
        event.preventDefault();
        return;
      }

      const submitButton =
        form.querySelector('.buy-buttons__submit') ||
        form.querySelector('[type="submit"]');

      if (submitButton instanceof HTMLButtonElement && submitButton.disabled) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      setBusy(submitButton, true);

      addToCart(form)
        .then((data) => {
          document.dispatchEvent(
            new CustomEvent('product_atc_via_form', {
              bubbles: true,
              detail: { items: data.items || data, source: 'buy-buttons' },
            })
          );
        })
        .catch((error) => {
          if (error?.code === 'quantity_rule') {
            clampQuantityToLimits(form);
            announceStatus(buyButtons, error.message);
          } else if (error?.code === 'inventory') {
            clampQuantityToLimits(form);
            announceInventoryError(buyButtons);
          } else {
            document.dispatchEvent(
              new CustomEvent('cart_error', {
                bubbles: true,
                detail: { message: error?.message || '', source: 'buy-buttons' },
              })
            );
          }

          flashError(submitButton);
        })
        .finally(() => {
          setBusy(submitButton, false);
        });
    },
    true
  );
})();
